import { useQuery } from '@tanstack/react-query';
import {
  STUDENT_MESSAGING_REFRESH_MS,
  isRefusal,
  studentMessagingApi,
  studentMessagingKeys,
  type StudentConversation,
} from '../api/studentMessaging';
import { useAuthStore } from '../store/auth';

/**
 * Liste des échanges avec les élèves, rafraîchie périodiquement. Partagée par
 * l'écran « Messages » et par le compteur de non-lus de la navigation : une
 * seule requête.
 */
export function useStudentConversations() {
  const userId = useAuthStore((s) => s.user?.id);
  return useQuery({
    queryKey: studentMessagingKeys.conversations(userId),
    queryFn: studentMessagingApi.getConversations,
    refetchInterval: STUDENT_MESSAGING_REFRESH_MS,
    refetchOnWindowFocus: true,
    staleTime: 0,
    retry: false,
    gcTime: 0,
  });
}

/**
 * Les échanges à montrer : aucun si le serveur refuse l'accès ou n'a encore
 * rien rendu ; ceux déjà reçus si seul le dernier rafraîchissement a échoué.
 */
export function shownStudentConversations(list: {
  data?: StudentConversation[];
  error: unknown;
}): StudentConversation[] {
  return isRefusal(list.error) ? [] : (list.data ?? []);
}

export function countUnread(conversations: StudentConversation[]): number {
  return conversations.reduce((total, c) => total + (c.unread || 0), 0);
}

/** Total des messages d'élèves non lus, pour la navigation. */
export function useStudentUnreadCount(): number {
  return countUnread(shownStudentConversations(useStudentConversations()));
}
